import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { RepairLocationAdvancedFilterComponent } from '../repair-location-advanced-filter/repair-location-advanced-filter.component';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { ToastrService } from '../../../services/toastr-service';
import { RepairLocationService } from '../../../services/repair-location.service';
import { RepairLocationFilterForm } from '../../../form-model/repair-location-filter.form.model';
import { RepairLocationSearchTextForm } from '../../../form-model/repair-location-search-text.form.model';
import { environment } from '../../../../environments/environment';
import { DownloadFilterCriteria, toFilter } from '../../../models/download-filter.model';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-button';

type FilterValue = string | string[];

@Component({
  selector: 'app-repair-location-filters',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, RepairLocationAdvancedFilterComponent, TagWithOverflowComponent],
  templateUrl: './repair-location-filters.component.html',
  styleUrl: './repair-location-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairLocationFiltersComponent implements AfterViewInit {
  showAdvanceFilters = false;
  isDownload = false;

  canAddNewRepairLocation = this.authPolicies.canAddRepairLocation && environment.Features.EnableRepairLocation;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairLocationFilterForm: FormGroup<RepairLocationFilterForm>;
    repairLocationSearchTextForm: FormGroup<RepairLocationSearchTextForm>;
  }>;

  taggedFilters: { key: string; value: FilterValue }[] = [];

  @ViewChild(RepairLocationAdvancedFilterComponent) advanceFilterComponent!: RepairLocationAdvancedFilterComponent;

  constructor(
    public readonly authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly repairLocationService: RepairLocationService,
  ) {}

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private ensureFlagControlsExist(): void {
    const filterForm = this.filterFormGroup.controls.repairLocationFilterForm;
    const searchForm = this.filterFormGroup.controls.repairLocationSearchTextForm;

    if (!filterForm.get('applyRepairLocationFilter')) {
      filterForm.addControl('applyRepairLocationFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!searchForm.get('appliedRepairLocationSearchTextFilter')) {
      searchForm.addControl('appliedRepairLocationSearchTextFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!filterForm.get('repairLocationCodeList')) {
      filterForm.addControl('repairLocationCodeList', this.formBuilder.control<string[] | null>(null));
    }
  }

  private setFilterFormValue(): void {
    const filterValue = this.repairLocationService.getRepairLocationFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.repairLocationFilterForm.patchValue(filterValue, { emitEvent: false });
      this.updateSelectedFiltersSection();
      setTimeout(() => {
        this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')?.setValue(true);
      });
    }
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')!.valueChanges.subscribe((value) => {
      if (value) {
        this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')!.setValue(null, { emitEvent: false });
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.repairLocationService.setRepairLocationFilterFormValue(
          this.filterFormGroup.controls.repairLocationFilterForm.getRawValue(),
        );
      }
    });
    this.filterFormGroup.controls.repairLocationSearchTextForm.get('appliedRepairLocationSearchTextFilter')!.valueChanges.subscribe(() => {
      this.updateSelectedFiltersSection();
    });
  }

  showAdvanceFiltersModal(): void {
    this.showAdvanceFilters = true;
  }

  applyAdvancedFilters(): void {
    this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')?.setValue(true);
  }

  clearAdvancedFilters(): void {
    const filterForm = this.filterFormGroup.controls.repairLocationFilterForm;
    filterForm.reset({ repairLocationCodeList: null });
    filterForm.get('applyRepairLocationFilter')?.setValue(true);
  }

  closeAdvanceFiltersModal(): void {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      const lastApplied = this.repairLocationService.getRepairLocationFilterFormValue();
      this.filterFormGroup.controls.repairLocationFilterForm.patchValue(
        { repairLocationCodeList: lastApplied?.repairLocationCodeList ?? null },
        { emitEvent: false },
      );
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')?.setValue(true);
    this.repairLocationService.setRepairLocationFilterFormValue(null);
    this.updateSelectedFiltersSection();
  }

  unselectRepairLocationCode(): (_: string) => void {
    return (code: string) => {
      const control = this.filterFormGroup.controls.repairLocationFilterForm.get('repairLocationCodeList');
      const currentValues = (control?.value as string[]) ?? [];
      const updatedValue = currentValues.filter((c) => c !== code);
      control?.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')?.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  updateSelectedFiltersSection(): void {
    const form = this.filterFormGroup.controls.repairLocationFilterForm;
    this.markAsTagged('Repair Location Code', form.get('repairLocationCodeList')?.value ?? null);
  }

  markAsTagged(key: string, newValue: FilterValue | null): void {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string): void {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string): FilterValue | null {
    return getSharedFilterValue(this.taggedFilters, key) as FilterValue | null;
  }

  getTaggedRepairLocationCodes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Repair Location Code');
    return filter && Array.isArray(filter.value) ? filter.value : [];
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onAddNewClicked(): void {
    this.router.navigate(['/master-data/repair-location/add-new']);
  }

  onDownload(): void {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.repairLocationService.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('repair-location'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.repairLocationFilterForm;
    const codes = form.get('repairLocationCodeList')?.value;
    if (codes && codes.length > 0) {
      const codeParts = codes.map((c) => c.split(' - ')[0]);
      filters.push(toFilter('code', codeParts));
    }
    return filters;
  }
}
